@if (_open(); as o) {
  <div class="fixed inset-0 z-40 flex justify-end">
    <!-- backdrop -->
    <div class="absolute inset-0 bg-black/30" (click)="close()"></div>
    <!-- panel -->
    <div class="relative z-10 flex h-full w-full max-w-3xl flex-col bg-white dark:bg-surface-1 shadow-2xl">
      <!-- header -->
      <div class="flex items-center justify-between border-b border-gray-200 dark:border-white/10 px-5 py-4">
        <div>
          <div class="flex items-center gap-2">
            <span class="h-2 w-2 rounded-full" [class]="statusDot(session()?.status || 'ended')"></span>
            <h2 class="text-base font-semibold text-gray-900 dark:text-white">
              {{ session()?.companyName || session()?.hostName || 'Session' }}
            </h2>
          </div>
          <p class="mt-0.5 font-mono text-xs text-gray-400">{{ o.sessionId }}</p>
        </div>
        <button type="button" (click)="close()"
          class="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10">
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <!-- tabs -->
      <div class="flex gap-1 border-b border-gray-200 dark:border-white/10 px-3">
        @for (t of panelTabs; track t) {
          <button type="button" (click)="setTab(t)"
            class="relative px-3 py-2.5 text-sm font-medium capitalize transition-colors"
            [class]="tab() === t ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'">
            {{ t }}
            @if (tab() === t) { <span class="absolute inset-x-2 -bottom-px h-0.5 rounded bg-indigo-500"></span> }
          </button>
        }
      </div>

      <div class="flex-1 overflow-y-auto p-5">
        @if (loading()) {
          <p class="text-sm text-gray-400">Loading…</p>
        }

        <!-- DETAILS -->
        @if (tab() === 'details' && session(); as s) {
          <div class="mb-3">
            <span class="text-[11px] uppercase text-gray-400">Status</span><br />
            <span class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset" [class]="statusClass(s.status)">{{ statusLabel(s.status) }}</span>
          </div>
          <dl class="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div><dt class="text-[11px] uppercase text-gray-400">Kind</dt><dd class="text-gray-700 dark:text-gray-200 capitalize">{{ s.kind }}</dd></div>
            <div><dt class="text-[11px] uppercase text-gray-400">AI Agent</dt><dd class="text-gray-700 dark:text-gray-200">{{ s.aiAgentName || 'AI' }} <span class="text-gray-400">({{ s.aiStatus || 'none' }})</span></dd></div>
            <div><dt class="text-[11px] uppercase text-gray-400">Model</dt><dd class="text-gray-700 dark:text-gray-200 break-all">{{ s.model || '—' }}</dd></div>
            <div><dt class="text-[11px] uppercase text-gray-400">Participants</dt><dd class="text-gray-700 dark:text-gray-200">{{ s.participantCount }} ({{ s.guestCount }} guests)</dd></div>
            <div><dt class="text-[11px] uppercase text-gray-400">Duration</dt><dd class="text-gray-700 dark:text-gray-200 tabular-nums">{{ s.endedAt ? durationSecToStr(s.durationSec) : liveDuration(s.startedAt, now()) }}</dd></div>
            <div><dt class="text-[11px] uppercase text-gray-400">Transcript</dt><dd class="text-gray-700 dark:text-gray-200">{{ s.transcriptStatus || 'none' }}</dd></div>
            <div><dt class="text-[11px] uppercase text-gray-400">Session Token</dt><dd class="text-gray-700 dark:text-gray-200">{{ s.hasSessionToken ? 'Present (' + (s.tokenCount || 0) + ')' : 'None' }}</dd></div>
            <div><dt class="text-[11px] uppercase text-gray-400">Started</dt><dd class="text-gray-700 dark:text-gray-200">{{ s.startedAt || '—' }}</dd></div>
            <div><dt class="text-[11px] uppercase text-gray-400">Ended</dt><dd class="text-gray-700 dark:text-gray-200">{{ s.endedAt || '—' }}</dd></div>
          </dl>
          @if (s.meetingUrl) {
            <div class="mt-3">
              <dt class="text-[11px] uppercase text-gray-400">Meeting URL</dt>
              <dd class="flex items-center gap-2"><span class="truncate text-gray-700 dark:text-gray-200">{{ s.meetingUrl }}</span>
                <button type="button" (click)="copyMeetingUrl()" class="text-indigo-500 text-xs hover:underline">Copy</button></dd>
            </div>
          }
          <div class="mt-5 border-t border-gray-100 dark:border-white/10 pt-4">
            <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Host Security</h3>
            <app-security-info [info]="s.security"></app-security-info>
          </div>
          <div class="mt-5 flex gap-2">
            <button type="button" (click)="disconnectRequested.emit(s)"
              class="rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-600">Disconnect</button>
            <button type="button" (click)="blacklistRequested.emit(s)" [disabled]="!s.security?.ip"
              class="rounded-lg bg-red-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-40">Blacklist IP</button>
          </div>
        }

        <!-- PARTICIPANTS -->
        @if (tab() === 'participants') {
          <div class="space-y-3">
            @if (actionError(); as err) {
              <p class="rounded-lg bg-red-50 dark:bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400">{{ err }}</p>
            }
            @for (p of participants(); track p.participantId) {
              <div class="rounded-lg border border-gray-200 dark:border-white/10 p-3">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase" [class]="roleBadge(p.role)">{{ p.role }}</span>
                    <span class="text-sm font-medium text-gray-800 dark:text-gray-100">{{ p.name }}</span>
                    <!-- Dialled in: the live panel is where an admin decides whether to mute or
                         remove someone, so it has to say which of them is on the phone. -->
                    @if (p.via === 'phone') {
                      <span class="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-teal-50 text-teal-700 dark:bg-teal-400/10 dark:text-teal-300">phone</span>
                    }
                    <span class="rounded-full px-1.5 py-0.5 text-[10px] font-medium" [class]="micChip(p).cls">{{ micChip(p).text }}</span>
                    @if (camChip(p); as cam) {
                      <span class="rounded-full px-1.5 py-0.5 text-[10px] font-medium" [class]="cam.cls">{{ cam.text }}</span>
                    }
                  </div>
                  <span class="text-xs text-gray-400 capitalize">{{ p.status }}</span>
                </div>
                <div class="mt-1 flex gap-4 text-[11px] text-gray-400">
                  <span>Joined: {{ p.joinedAt || '—' }}</span>
                  <span>Left: {{ p.leftAt || '—' }}</span>
                </div>
                @if (canMute(p) || canRemove(p)) {
                  <div class="mt-2 flex gap-2">
                    @if (canMute(p)) {
                      <button type="button" (click)="toggleMute(p)" [disabled]="actionBusy() === p.participantId"
                        class="rounded-md px-2.5 py-1 text-xs font-medium disabled:opacity-40"
                        [class]="micOff(p)
                          ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/20'
                          : 'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/20'">
                        {{ micOff(p) ? 'Unmute' : 'Mute' }}
                      </button>
                    }
                    @if (canRemove(p)) {
                      <button type="button" (click)="removeGuest(p)" [disabled]="actionBusy() === p.participantId"
                        class="rounded-md px-2.5 py-1 text-xs font-medium disabled:opacity-40"
                        [class]="pendingRemove() === p.participantId
                          ? 'bg-red-500 text-white hover:bg-red-600'
                          : 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20'">
                        {{ pendingRemove() === p.participantId ? 'Confirm remove?' : 'Remove' }}
                      </button>
                    }
                  </div>
                }
                @if (p.security) {
                  <details class="mt-2">
                    <summary class="cursor-pointer text-xs text-indigo-500">Security details</summary>
                    <div class="mt-2"><app-security-info [info]="p.security"></app-security-info></div>
                  </details>
                }
              </div>
            } @empty {
              <p class="text-sm text-gray-400">No participants recorded.</p>
            }
          </div>
        }

      </div>
    </div>
  </div>
}
